
import { Mixins, Component, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import Context from "@/mixins/context";
import ExternalAuthMixin from "@/components/app/global/payments/externalAuthMixin.vue";
import RequestPromises from "@/mixins/requestPromises";
import { IInvoice } from "@/models/invoices";
import { IModalComponent } from "@/models/modal";
import { IState } from "@/store";
import { IStatus } from "@/models/statuses";
import { DataModules } from "@/store/modules/data";
import { BASE_URL } from "@/store/modules/api";
import { InvoiceStatus, InvoiceStatusGroups } from "@/data/enums/invoice";
import { InvoiceCategoryCode } from "@/models/invoices";
import { ModalTypes } from "@/data/enums";
import { convertObjAb2Json, downloadBlob } from "@/helpers/file";
import { mapGetters, mapState } from "vuex";
import {
  IInvoiceProvider,
  IInvoiceProviderMethods
} from "@/models/providers/invoice";
import PurchaseEventMixin from "@/mixins/purchaseEventMixin";
import { IBrand } from "@/models/brands";
import { IProvider } from "@/models/providers";
import { ContractStatusCodes as CSC } from "@/models/contracts";
import _ from "@/boot/lodash";

@Component({
  computed: {
    ...mapGetters({
      brandId: "brand/id"
    }),
    ...mapState({
      invoiceStatuses: (state: IState) => state.constants.statuses.invoice,
      invoiceScope(state: IState, getters) {
        return getters[`data/${DataModules.INVOICES}/scope`](this.invoiceId);
      },
      invoice(state: IState, getters): IInvoice {
        return getters["data/single"]({
          id: this.invoiceId,
          scope: this.invoiceScope,
          storeModule: DataModules.INVOICES
        });
      },
      isPaid(state: IState, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isPaid`](this.invoice);
      },
      isPayable(state: IState, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isPayable`](this.invoice);
      },
      isRefunded(state: IState, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isRefunded`](this.invoice);
      },
      isCancelled(state: IState, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isCancelled`](
          this.invoice
        );
      },
      hasPendingPayments(state, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/hasPendingPayments`](
          this.invoice
        );
      },
      hasCreditRefundOptions(state, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/hasCreditRefundOptions`](
          this.invoice
        );
      },
      isOverdue(state: IState, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isOverdue`](this.invoice);
      },
      isCreditNote(state: IState, getters): boolean {
        return getters[`data/${DataModules.INVOICES}/isCreditNote`](
          this.invoice
        );
      }
    })
  }
})
export default class InvoiceProvider
  extends Mixins(
    RequestPromises,
    Context,
    ExternalAuthMixin,
    PurchaseEventMixin
  )
  implements IProvider {
  // Props

  @Prop({ required: true })
  public invoiceId!: IInvoice["id"];

  @Prop({ default: false })
  public isBasket!: boolean;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: InvoiceProvider) => {
      const name = vm.isAdmin ? "adminClientInvoices" : "clientInvoices";
      return vm.$router.push({ name }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public brandId!: IBrand["id"];
  public hasCreditRefundOptions!: boolean;
  public hasPendingPayments!: boolean;
  public hasRequestError = false;
  public invoice!: IInvoice;
  public invoiceScope!: string;
  public invoiceStatuses!: IStatus[];
  public isCancelled!: boolean;
  public isCreditNote!: boolean;
  public isLoading = true;
  public isOverdue!: boolean;
  public isPaid!: boolean;
  public isPayable!: boolean;
  public isProcessing = {};
  public isRefunded!: boolean;
  public isReloading = false;

  // Life-cycle Methods

  public get status(): IStatus {
    if (!this.invoice) {
      return {} as IStatus;
    }
    const status = _.find(
      this.invoiceStatuses,
      s => s.id === _.get(this.invoice, "status_id")
    );
    return { code: "", ...status } as IStatus;
  }

  public get invoiceDataContent() {
    return _.get(this.invoice, "current_data.content", {});
  }

  public get imgURL() {
    const url = _.get(this, "invoiceDataContent.image");
    return url ? `${BASE_URL}${url}` : null;
  }

  public get contentAvailable() {
    return this.$userCan("staff_get_invoice");
  }

  public get inactiveCProds() {
    if (!this.isAdmin) return {} as IInvoiceProvider["inactiveCProds"];

    return this.invoice?.products?.reduce((acc, product) => {
      const canReactivate =
        !_.get(product.contracts_product, "pro_rata_pending") &&
        [CSC.CANCELLED, CSC.CLOSED].includes(
          product.contracts_product?.status?.code as CSC
        );
      if (canReactivate)
        acc[product.id] = {
          invoiceProduct: product,
          contractProduct: product.contracts_product
        };

      return acc;
    }, {}) as IInvoiceProvider["inactiveCProds"];
  }

  public get scope() {
    return `$invoice_${this.invoiceId}_statuses`;
  }

  public get hasPayments() {
    return !_.isEmpty(_.get(this.invoice, "payments"));
  }

  public get hasPaymentMethod() {
    return !!(
      _.get(this.invoice, "payment_details_id") ||
      _.get(this.invoice, "gateway_id")
    );
  }

  public get isDuplicated() {
    return !!_.get(this.invoice, "duplicated_with_invoice_id");
  }

  public get isDuplicate() {
    return !!_.get(this.invoice, "duplicate_from_invoice_id");
  }

  public get isShareable() {
    return (
      !_.get(this.invoice, "delegate_related") &&
      !this.$store.getters["isGuestContext"]
    );
  }

  public get isStaged() {
    return !!_.get(this.invoice, "staged_import");
  }

  public get hasCompany() {
    return !_.isEmpty(this.invoiceDataContent.client_company);
  }

  public get hasAddress() {
    return !_.isEmpty(this.invoiceDataContent.client_address);
  }

  public get isRefundCreditNote() {
    return [InvoiceCategoryCode.CREDIT_NOTE_FOR_REFUND].includes(
      _.get(this.invoice, "category.slug")
    );
  }

  // Allow recalculation if FULLY unpaid
  public get canRecalculateTax() {
    return (
      InvoiceStatusGroups.UNPAID.includes(_.get(this.invoice, "status.code")) &&
      !_.get(this.invoice, "paid_amount_converted")
    );
  }

  public get isClientDeleted() {
    return !_.get(this.invoice, "client", false);
  }

  public get referrer() {
    return this.invoice?.account?.affiliate_referral?.affiliate_account?.account
      ?.client?.[0];
  }

  @Provide("invoiceProviderMethods")
  public get providerMethods(): IInvoiceProviderMethods {
    return {
      createOrderTemplate: this.createOrderTemplate,
      download: this.download,
      openChangePaymentMethodModal: this.openChangePaymentMethodModal,
      openCreditRefundModal: this.openCreditRefundModal,
      openDueDateModal: this.openDueDateModal,
      openRegenerateInvoiceModal: this.openRegenerateInvoiceModal,
      openSendEmailModal: this.openSendEmailModal,
      pay: this.pay,
      reloadData: this.reloadData,
      share: this.share
    };
  }

  @Provide("invoiceProvider")
  public providerData(): IInvoiceProvider {
    return {
      brandId: this.brandId,
      canRecalculateTax: this.canRecalculateTax,
      contentAvailable: this.contentAvailable,
      hasAddress: this.hasAddress,
      hasCompany: this.hasCompany,
      hasCreditRefundOptions: this.hasCreditRefundOptions,
      hasPaymentMethod: this.hasPaymentMethod,
      hasPendingPayments: this.hasPendingPayments,
      hasRequestError: this.hasRequestError,
      imgURL: this.imgURL,
      inactiveCProds: this.inactiveCProds,
      invoice: this.invoice,
      invoiceDataContent: this.invoiceDataContent,
      invoiceId: this.invoiceId,
      isCancelled: this.isCancelled,
      isClient: this.isClient,
      isClientDeleted: this.isClientDeleted,
      isCreditNote: this.isCreditNote,
      isDuplicate: this.isDuplicate,
      isDuplicated: this.isDuplicated,
      isLoading: this.isLoading,
      isOverdue: this.isOverdue,
      isPaid: this.isPaid,
      isPayable: this.isPayable,
      isProcessing: this.isProcessing,
      isRefundCreditNote: this.isRefundCreditNote,
      isRefunded: this.isRefunded,
      isReloading: this.isReloading,
      isShareable: this.isShareable,
      isStaged: this.isStaged,
      referrer: this.referrer,
      status: this.status
    };
  }

  public async created() {
    this.checkExternalAuthQueryParams();

    if (!this.contentAvailable) {
      this.isLoading = false;
      return;
    }
    this.$store.dispatch("constants/getStatuses", "invoice");
    this.$store.dispatch("constants/getCountries");

    this.$store.commit("ui/breadcrumb/add", {
      key: "invoice",
      value: this.invoiceId
    });

    this.attachGlobalBusEvents();

    this.loadData();

    this.$bus.$on("refreshInvoiceData", () => {
      this.reloadData();
    });
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public reloadAtBusEvent({ invoiceId }) {
    if (invoiceId !== this.invoiceId) return;
    this.reloadData();
  }

  public beforeDestroy() {
    this.$store.commit("ui/unsetMetaProps", ["invoiceNum"]);
    this.$store.dispatch("data/binList", {
      vm: this,
      scope: this.scope,
      storeModule: DataModules.STATUSES
    });
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.INVOICES,
      scope: this.invoiceScope,
      vm: this
    });

    this.$bus.$off("refreshInvoiceData");
    this.$bus.$off(
      "init-payment-invoice-payment-method-changed",
      this.reloadAtBusEvent
    );
    this.$bus.$off("init-payment-invoice-paid", this.reloadAtBusEvent);
    this.$bus.$off("autopay-invoice-success", this.reloadAtBusEvent);
  }

  public attachGlobalBusEvents() {
    this.$bus.$on(
      "init-payment-invoice-payment-method-changed",
      this.reloadAtBusEvent
    );
    this.$bus.$on("init-payment-invoice-paid", this.reloadAtBusEvent);
    this.$bus.$on("autopay-invoice-success", this.reloadAtBusEvent);
  }

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await this.getInvoiceData(ignoreStored);
      if (this.invoice.status.code === InvoiceStatus.DRAFT && !this.isBasket) {
        this.$router.push({
          name: "adminOrderBasketView",
          params: {
            basketId: this.invoiceId
          }
        });
      }

      this.checkPurchaseEvent(this.invoice);
      this.$store.commit("ui/setMetaProps", {
        invoiceNum: _.get(this.invoice, "number")
      });
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
      if (!this.$store.getters["api/is404"](error)) return;
      this.onNotFound && this.onNotFound(this);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public getInvoiceData(ignoreStored) {
    return this.$store.dispatch(`data/${DataModules.INVOICES}/get`, {
      id: this.invoiceId,
      scope: this.invoiceScope,
      params: {
        with: _.concat(
          [
            "account.affiliate_referral.affiliate_account.account.client",
            "account.user",
            "affiliate_commissions",
            "client",
            "data",
            "duplicate_invoice", // Required if duplicate,
            "gateway",
            "import.credentials.source",
            "original_invoice", // Required if duplicated
            "payment_details",
            "payments.gateway",
            "payments.payment_details",
            "payments.payment_type",
            "promotions",
            "status",
            "taxes",
            "taxes.tax_tag_data"
          ],
          this.isBasket
            ? ["products", "products.product.image"]
            : ["products.contracts_product"]
        ).join(",")
      },
      ignoreStored,
      vm: this
    });
  }

  public share() {
    if (!this.$userCan("store_shared_resources")) return;
    this.$store.dispatch(`data/${DataModules.SHARES}/openInvoiceShareModal`, {
      props: {
        objectId: this.invoiceId,
        allowPayment: [
          InvoiceStatus.UNPAID,
          InvoiceStatus.ADJUSTED,
          InvoiceStatus.OVERDUE
        ].includes(_.get(this.invoice, "status.code"))
      }
    });
  }

  public download() {
    this.$set(this.isProcessing, "download", true);
    this.$store
      .dispatch(`data/${DataModules.INVOICES_PDFS}/download`, {
        vm: this,
        invoiceId: this.invoiceId
      })
      .then(data => {
        const blob = new Blob([data], {
          type: "application/pdf;"
        });
        const filename = `${this.invoice.number}.pdf`;
        downloadBlob(blob, filename);
      })
      .catch(error => {
        // Convert ArrayBuffer data to JSON
        convertObjAb2Json(error, "response.data");
        // Check for 404 (not found)
        if (_.get(error, "response.status") === 404) {
          return this.$store.dispatch("toast/warning", {
            message: this.$t(
              "_sentence.pdf_is_being_generated_and_will_be_available_shortly"
            )
          });
        }
        this.$store.dispatch("api/handleError", error);
      })
      .finally(() => {
        this.$delete(this.isProcessing, "download");
      });
  }

  public async pay(config = {}) {
    const modal = await this.$store.dispatch("ui/open/windowModal", {
      config: _.merge(
        {
          component: () =>
            import("@/components/app/global/payments/invoicePaymentModal.vue"),
          props: {
            invoice: this.invoice
          },
          width: 740,
          canCancel: ["button"],
          events: {
            success: () => {
              modal.close();
              this.$emit("success");
              this.reloadData();
            }
          }
        },
        config
      )
    });
    return modal;
  }

  public openDueDateModal(): Promise<IModalComponent> {
    return this.$store.dispatch("ui/openModal", {
      type: ModalTypes.WINDOW,
      config: {
        parent: this, // Parent required for Inject() to work
        component: () =>
          import("@/components/app/global/invoices/invoiceDueDateModal.vue"),
        width: 440,
        props: {
          dueDate: this.$moment(this.invoice.due_date).toDate(),
          invoiceId: this.invoiceId
        },
        events: {
          changed: () => this.reloadData()
        }
      }
    });
  }

  public openChangePaymentMethodModal(config = {}) {
    this.$store.dispatch("ui/open/windowModal", {
      config: this.$_.merge(
        {
          width: 740,
          component: () =>
            import(
              "@/components/app/global/invoices/invoiceChangePaymentMethodModal.vue"
            ),
          events: {
            "payment-method-changed": () => this.reloadData()
          },
          props: {
            invoice: this.invoice
          }
        },
        config
      )
    });
  }

  public openCreditRefundModal() {
    this.$store.dispatch("ui/open/slideModal", {
      config: {
        component: () =>
          import(
            "@/components/app/global/invoices/invoiceCreditRefundModal.vue"
          ),
        props: {
          invoice: this.invoice
        },
        events: {
          "invoice-updated": () => this.reloadData()
        }
      }
    });
  }

  public openSendEmailModal() {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES}/openSendEmailModal`,
      {
        width: 480,
        props: {
          type: "invoice",
          objectId: _.get(this.invoice, "id"),
          title: this.$t("_action.send_object", {
            object: (this.$t("_.object_email", {
              object: (this.$t("_.invoice") as string).toLowerCase()
            }) as string).toLowerCase()
          })
        }
      }
    );
  }

  public openRegenerateInvoiceModal() {
    return this.$store.dispatch(
      `data/${DataModules.INVOICES}/openRegenerateInvoiceModal`,
      {
        parent: this,
        props: {
          invoiceId: _.get(this.invoice, "id")
        }
      }
    );
  }

  public createOrderTemplate() {
    this.$store.dispatch(
      `data/${DataModules.ORDERS_TEMPLATES}/openManageOrderTemplateModal`,
      {
        props: {
          orderId: this.invoice.id
        },
        events: {
          success: () => {
            this.$store.dispatch("toast/show", {
              message: this.$t("_.order_template_successfully_created")
            });
            this.$router.push({
              name: "orderTemplatesSettings"
            });
          }
        }
      }
    );
  }

  public async refreshInvoiceData() {
    try {
      this.$set(this.isProcessing, "refreshingInvoiceData", true);
      await this.getInvoiceData(true);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.$delete(this.isProcessing, "refreshingInvoiceData");
    }
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $invoiceData: this.providerData(),
        $invoiceMethods: this.providerMethods
      })
    );
  }
}
